import { useMemo } from 'react';

import type { DATypeConfigInfo } from '~/server_shared/openapi/data-contracts';

import { useClaim } from '../ClaimContainer';
import useDataFetcher from '../useDataFetcher';

import type { DamageAssessmentConfig } from './useDamageAssessmentConfig';

// Minimal shape we need from each exposure — accepts both ExposureModel and the lighter
// ExposureDisplay shape returned by useFetchExposures (ClaimPolymorph['exposures']).
interface ExposureLike {
  id: number;
  damage_assessment_type?: string | null;
}

interface UseDAConfigsByExposureReturn {
  /** Map<exposure_id, DamageAssessmentConfig> built by joining each exposure's
   *  damage_assessment_type to the org's DA type configs. */
  exposureToDAConfig: Map<number, DamageAssessmentConfig>;
  /** True when at least one of the org's DA type configs has `allowed_payable_types`
   *  set. Used as the per-row payable behavior activation guard so that orgs without
   *  any explicit configuration see no UI/save change. */
  anyDATypeConfiguresPayable: boolean;
  isLoading: boolean;
}

/**
 * Bulk variant of useDamageAssessmentConfig: returns DA config keyed by exposure id
 * for all of the claim's exposures, plus an activation guard that lets callers opt
 * the new per-row payable behavior in only when at least one DA type configures
 * allowed_payable_types. Fast Cover orgs (no DA configs) and orgs without the new
 * field set return an empty map and `anyDATypeConfiguresPayable === false`.
 */
export const useDAConfigsByExposure = (
  exposures: readonly ExposureLike[] | undefined
): UseDAConfigsByExposureReturn => {
  const { claim } = useClaim({ claimRequired: true });

  const url = `/api/v1/organizations/${claim.organization_id}/damage_assessment_type_configs`;
  const { isLoading, data: daTypeConfigs } = useDataFetcher<DATypeConfigInfo[]>(url);

  const exposureToDAConfig = useMemo(() => {
    const map = new Map<number, DamageAssessmentConfig>();
    if (!exposures || !daTypeConfigs) return map;
    for (const exposure of exposures) {
      const daTypeKey = exposure.damage_assessment_type;
      if (!daTypeKey) continue;
      const matching = daTypeConfigs.find((c) => c.key === daTypeKey && !c.is_disabled);
      if (matching?.config) map.set(exposure.id, matching.config);
    }
    return map;
  }, [exposures, daTypeConfigs]);

  const anyDATypeConfiguresPayable = useMemo(
    () =>
      Boolean(
        daTypeConfigs?.some(
          (c) =>
            !c.is_disabled && c.config?.allowed_payable_types !== null && c.config?.allowed_payable_types !== undefined
        )
      ),
    [daTypeConfigs]
  );

  return { exposureToDAConfig, anyDATypeConfiguresPayable, isLoading };
};
